/*
 * $Id:: FilterActionButtons.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/core/styles';

import { ReactComponent as CaretBottom } from '../../assets/images/caretBottom.svg';
import { ReactComponent as WarningIcon } from '../../assets/images/warning_icon_orange.svg';
import { t } from '../../providers';

const useStyles = makeStyles(() => ({
    actionButtonsArea: {
        marginBottom: '10px',
        paddingTop: '23px',
        '@media (max-width:1100px)': {
            marginRight: '10px',
            paddingTop: '15px',
            width: '100%',
            marginBottom: '10px !important',
        },
        '@media (max-width:960px)': {
            width: 'auto',
        },
        '@media (max-width:1505px)': {
            marginBottom: '10px',
            paddingTop: '15px',
        },
        '@media (max-width:1540px)': {
            paddingTop: '15px',
        },
    },
    textInput: {
        height: '2rem',
        minWidth: '9rem',
        border: '1px solid #CDCED9',
        borderRadius: '4px',
        backgroundColor: '#FFFFFF',
        marginLeft: '10px',
        '@media (max-width:1505px)': {
            marginLeft: '0px',
        },
        '@media (max-width:1540px)': {
            marginLeft: '0px',
        },
        '@media (max-width:1100px)': {
            marginLeft: '0px',
        },
        marginRight: '0px',
        '&.MuiInputBase-root.Mui-focused': {
            border: '1px solid #CDCED9',
            backgroundColor: '#FFFFFF',
        },
    },
    disabledActionButton: {
        border: '1px solid #E7E7ED',
        '&:active': {
            border: '1px solid #E7E7ED',
        },
    },
    initiateServices: { width: '11rem', marginBottom: '5px' },
    exportAction: {
        width: '10.5rem',
        paddingRight: '10px',
        '@media (max-width:1100px)': {
            width: '100%',
        },
        '@media (max-width:960px)': {
            width: '9rem',
        },
        '& .MuiSelect-selectMenu': {
            '&:focus-visible': {
                backgroundColor: 'rgba(209, 224, 194, 0.2)',
                outline: 'none',
                border: '2px dashed #4F7E1D',
                borderRadius: '3px',
                margin: '0 -10px 0px -1px',
            },
        },
    },
    buttonMargin: {
        marginRight: '10px',
    },
    exportLoader: {
        border: '1px solid #D6D8DB',
        float: 'left',
        background: '#E2E3E5',
        color: '#383D41',
        padding: '5px 10px',
        borderRadius: '2px',
        fontSize: '0.875rem',
        textAlign: 'center',
        verticalAlign: 'middle',
        letterSpacing: '-0.2px',
        fontWeight: 'normal',
        fontStyle: 'normal',
        lineHeight: '142%',
        '& span': {
            marginRight: '8px',
        },
        '@media (max-width:600px)': {
            marginBottom: '10px',
            marginRight: '10px',
        },
    },
    errorMessage: {
        background: '#FEF3C7',
        borderRadius: '2px',
        color: '#3D1B0A',
        fontSize: '0.875rem',
        textAlign: 'center',
        verticalAlign: 'middle',
        letterSpacing: '-0.2px',
        fontWeight: 'normal',
        fontStyle: 'normal',
        lineHeight: '142%',
        float: 'left',
        padding: '6px 10px',
        '& span': {
            marginLeft: '5.65px',
        },
        '@media (max-width:600px)': {
            marginRight: '10px',
        },
    },
    disabledMenuItem: {
        color: '#5D5D5D',
        opacity: '1 !important',
    },
}));

const FilterActionButtons = ({
    filterItems,
    onInitiateServices,
    applicationType,
    exportTypes,
    exportError,
    exportLoading,
    noDataToExport = false,
}) => {
    const classes = useStyles();
    const exportTps = exportTypes();

    const actions = filterItems && filterItems.getInitiateActions(applicationType);
    const [exportIsOpen, setExportIsOpen] = useState(false);

    const openExport = () => {
        setExportIsOpen(true);
    };

    const closeExport = () => {
        setExportIsOpen(false);
    };

    return (
        <div className={classes.actionButtonsArea}>
            {exportLoading && (
                <div className={classes.exportLoader}>
                    <span>{t(exportLoading)}</span>
                    <CircularProgress style={{ width: '20px', height: '20px', verticalAlign: 'sub' }} />
                </div>
            )}
            {exportError && (
                <div className={classes.errorMessage}>
                    <WarningIcon style={{ width: '12.71px', height: '11.34px' }} aria-hidden="true" focusable="false" />
                    <span>{t('error.exporting.data')}</span>
                </div>
            )}
            {!!onInitiateServices && (
                <Select
                    id={'initiate-eServices-input'}
                    className={`${classes.textInput} ${classes.initiateServices} ${classes.disabledActionButton}`}
                    displayEmpty
                    disabled
                    IconComponent={CaretBottom}
                    value={''}
                    renderValue={(_value) => {
                        return t('initiate.eservices');
                    }}
                    inputProps={{ 'aria-haspopup': 'listbox' }}
                    onChange={(e) => onInitiateServices(e.target.value)}>
                    {actions.map((option, idx) => (
                        <MenuItem id={`initiate-eServices-option-${idx}`} key={`${option}_${idx}`} value={option} role="option">
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            )}
            {exportTps && !!exportTps.length && (
                <Select
                    SelectDisplayProps={{
                        'aria-label': `${t('export.results')} ${t('result.download.automatically')}`,
                        role: 'combobox',
                        'aria-controls': 'export-select',
                        'aria-expanded': exportIsOpen,
                        'aria-labelledby': undefined,
                    }}
                    id={'export-results-input'}
                    className={`${classes.textInput} ${classes.exportAction}`}
                    displayEmpty
                    onOpen={openExport}
                    onClose={closeExport}
                    IconComponent={CaretBottom}
                    value={''}
                    disabled={exportLoading || noDataToExport}
                    renderValue={(_value) => {
                        return t('export.results');
                    }}
                    MenuProps={{
                        'aria-haspopup': true,
                        MenuListProps: { role: 'listbox', id: 'export-select', 'aria-labelledby': 'export-results-input' },
                    }}>
                    {exportTps.map((option, idx) => (
                        <MenuItem id={`export-results-option-${idx}`} key={`${option.label}_${idx}`} value={option.label} onClick={option.action}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            )}
        </div>
    );
};

export default FilterActionButtons;
